import { useEffect, useRef } from 'react';
import { getRiskCommandsOrThrow } from '../orchestration/riskCommandRef';

const ACTUAL_RISK_COHERENCE_ERROR = 'Actual Risk Overview panels did not load coherently.';
const BOOTSTRAP_ATTEMPTS = 2;
const BOOTSTRAP_RETRY_DELAY_MS = 250;

// The completed core context a load leaves in lastCoreLoadKeyRef.
export const coreLoadPublicKey = (userUid, selectedFactorModel, requestedFactorDataModel) => [
    userUid,
    selectedFactorModel,
    requestedFactorDataModel ?? '',
].join('|');

export default function useCoreLoadBootstrap({
    userUid,
    userData,
    bootstrapSelectionsReady = true,
    suspendCoreLoad = false,
    selectedFactorModel,
    requestedFactorDataModel,
    lastCoreLoadKeyRef,
    publicationCoreLoadCommitVersionRef = null,
    coreLoadInFlightRef,
    exclusiveCoreLoadOwnerRef,
    beginCoreLoadVisual,
    endCoreLoadVisual,
    riskCommandsRef,
    markPendingRefreshFromLoadResult,
    onCommitWatchlistBootstrapRevision,
    consumeQueuedPriceRefresh,
    refreshPriceDataRef,
    watchlistActualSyncVersion = 0,
}) {
    const mountedRef = useRef(true);
    const runOwnerRef = useRef(0);
    const activeVisualTokenRef = useRef(null);
    const bootstrapKeyRef = useRef('');
    const bootstrapInFlightRef = useRef(false);
    const bootstrapCommittedRef = useRef(false);
    const committedPublicKeyRef = useRef(lastCoreLoadKeyRef.current || '');
    const interruptedPublicationKeyRef = useRef('');
    const interruptedPublicationCommitVersionRef = useRef(null);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const userDataReady = Boolean(userData);
    useEffect(() => {
        if (
            !userUid
            || !userDataReady
            || !bootstrapSelectionsReady
        ) {
            runOwnerRef.current += 1;
            bootstrapKeyRef.current = '';
            bootstrapInFlightRef.current = false;
            bootstrapCommittedRef.current = false;
            coreLoadInFlightRef.current = false;
            lastCoreLoadKeyRef.current = '';
            committedPublicKeyRef.current = '';
            interruptedPublicationKeyRef.current = '';
            interruptedPublicationCommitVersionRef.current = null;
            if (activeVisualTokenRef.current != null) {
                endCoreLoadVisual(activeVisualTokenRef.current);
                activeVisualTokenRef.current = null;
            }
            return;
        }
        const publicKey = coreLoadPublicKey(userUid, selectedFactorModel, requestedFactorDataModel);
        const interruptedPublicationPending = Boolean(
            interruptedPublicationKeyRef.current,
        );
        const interruptedPublicationCommitted = (
            interruptedPublicationPending
            && Number(publicationCoreLoadCommitVersionRef?.current || 0)
                > Number(interruptedPublicationCommitVersionRef.current || 0)
        );
        if (
            interruptedPublicationPending
            && !interruptedPublicationCommitted
            && !exclusiveCoreLoadOwnerRef?.current
        ) {
            // Neither a normal suspension nor a factor-model change may
            // bypass a failed exact-revision publication handoff.
            return;
        }
        if (interruptedPublicationCommitted) {
            interruptedPublicationKeyRef.current = '';
            interruptedPublicationCommitVersionRef.current = null;
            if (lastCoreLoadKeyRef.current === publicKey) {
                committedPublicKeyRef.current = publicKey;
                bootstrapKeyRef.current = `${publicKey}|${watchlistActualSyncVersion}`;
                bootstrapCommittedRef.current = true;
                return;
            }
        }
        if (suspendCoreLoad || exclusiveCoreLoadOwnerRef?.current) {
            const interruptedBootstrap = bootstrapInFlightRef.current;
            const publicationOwnsLoad = Boolean(exclusiveCoreLoadOwnerRef?.current);
            runOwnerRef.current += 1;
            bootstrapKeyRef.current = '';
            bootstrapInFlightRef.current = false;
            bootstrapCommittedRef.current = false;
            coreLoadInFlightRef.current = false;
            // An exclusive publication owner is itself loading the next core
            // snapshot. Preserve the last completed public key so releasing
            // that owner does not immediately issue a duplicate bootstrap.
            // A normal suspended context change has a different public key
            // and will therefore still load when its gate reopens.
            if (publicationOwnsLoad && interruptedBootstrap) {
                interruptedPublicationKeyRef.current = publicKey;
                interruptedPublicationCommitVersionRef.current = Number(
                    publicationCoreLoadCommitVersionRef?.current || 0,
                );
                lastCoreLoadKeyRef.current = committedPublicKeyRef.current;
            } else if (!publicationOwnsLoad && !interruptedPublicationKeyRef.current) {
                lastCoreLoadKeyRef.current = '';
            }
            if (activeVisualTokenRef.current != null) {
                endCoreLoadVisual(activeVisualTokenRef.current);
                activeVisualTokenRef.current = null;
            }
            return;
        }
        const key = `${publicKey}|${watchlistActualSyncVersion}`;
        if (
            bootstrapKeyRef.current === ''
            && lastCoreLoadKeyRef.current === publicKey
            && !coreLoadInFlightRef.current
        ) {
            // The publication-owned load committed this same core context
            // while bootstrap was fenced. Adopt its result instead of
            // re-reading and repainting the dashboard after 100%.
            bootstrapKeyRef.current = key;
            bootstrapCommittedRef.current = true;
            committedPublicKeyRef.current = publicKey;
            return;
        }
        if (bootstrapKeyRef.current === key) {
            // Dashboard session reset effects are replayed in development
            // StrictMode. Restore their public mirrors without creating a
            // second network owner for the same authenticated bootstrap.
            if (bootstrapInFlightRef.current || bootstrapCommittedRef.current) {
                lastCoreLoadKeyRef.current = publicKey;
                coreLoadInFlightRef.current = bootstrapInFlightRef.current;
            }
            return;
        }
        bootstrapKeyRef.current = key;
        bootstrapInFlightRef.current = true;
        bootstrapCommittedRef.current = false;
        lastCoreLoadKeyRef.current = publicKey;
        coreLoadInFlightRef.current = true;
        const runOwner = ++runOwnerRef.current;
        const ownsRun = () => (
            mountedRef.current
            && runOwnerRef.current === runOwner
            && bootstrapKeyRef.current === key
            && !exclusiveCoreLoadOwnerRef?.current
        );
        const coreLoadVisualToken = beginCoreLoadVisual();
        activeVisualTokenRef.current = coreLoadVisualToken;
        const commands = getRiskCommandsOrThrow(
            riskCommandsRef,
            'useCoreLoadBootstrap',
            ['refreshRiskSurface', 'refreshSelectedOverview'],
        );
        const loadOptions = {
            reason: 'init',
            includeSelectedOverview: false,
            deferSecondary: true,
        };
        let loadCommitted = false;
        const run = (async () => {
            for (let attempt = 0; attempt < BOOTSTRAP_ATTEMPTS; attempt += 1) {
                if (!ownsRun()) {
                    return null;
                }
                try {
                    return await commands.refreshRiskSurface(loadOptions);
                } catch (error) {
                    if (!ownsRun()) {
                        return null;
                    }
                    if (
                        error?.message !== ACTUAL_RISK_COHERENCE_ERROR
                        || attempt + 1 >= BOOTSTRAP_ATTEMPTS
                    ) {
                        throw error;
                    }
                    // A refused atomic publication is safe to retry as a whole.
                    // Recheck ownership after backoff before issuing any request.
                    await new Promise((resolve) => setTimeout(resolve, BOOTSTRAP_RETRY_DELAY_MS));
                }
            }
            return null;
        })();
        run.then((loadResult) => {
            if (!ownsRun() || !loadResult || loadResult.matrixStatus === 'cancelled') {
                return;
            }
            if (typeof onCommitWatchlistBootstrapRevision === 'function') {
                onCommitWatchlistBootstrapRevision(
                    loadResult?.watchlistSurface?.watchlistRevision,
                );
            }
            loadCommitted = true;
            bootstrapCommittedRef.current = true;
            committedPublicKeyRef.current = publicKey;
            lastCoreLoadKeyRef.current = publicKey;
            markPendingRefreshFromLoadResult(loadResult);
            if (loadResult?.deferredCompletion?.then) {
                void loadResult.deferredCompletion.then((deferredLoadResult) => {
                    if (ownsRun()) {
                        markPendingRefreshFromLoadResult(deferredLoadResult);
                    }
                });
            }
        }).catch((error) => {
            if (ownsRun()) {
                console.error('Core risk-surface load failed:', error);
            }
        }).finally(() => {
            endCoreLoadVisual(coreLoadVisualToken);
            if (activeVisualTokenRef.current === coreLoadVisualToken) {
                activeVisualTokenRef.current = null;
            }
            if (ownsRun()) {
                bootstrapInFlightRef.current = false;
                coreLoadInFlightRef.current = false;
                if (!loadCommitted) {
                    if (lastCoreLoadKeyRef.current === publicKey) {
                        lastCoreLoadKeyRef.current = committedPublicKeyRef.current;
                    }
                    return;
                }
                const queuedOptions = consumeQueuedPriceRefresh();
                if (queuedOptions) {
                    // The queued price refresh ends with its own selected
                    // Overview refresh.
                    void refreshPriceDataRef.current?.(queuedOptions);
                } else {
                    // A reload under a new factor context changes an
                    // existing Risk Overview selection's risk.
                    commands.refreshSelectedOverview({ reason: 'core_load_bootstrap' });
                }
            }
        });
    }, [
        beginCoreLoadVisual,
        consumeQueuedPriceRefresh,
        coreLoadInFlightRef,
        endCoreLoadVisual,
        exclusiveCoreLoadOwnerRef,
        lastCoreLoadKeyRef,
        markPendingRefreshFromLoadResult,
        onCommitWatchlistBootstrapRevision,
        publicationCoreLoadCommitVersionRef,
        refreshPriceDataRef,
        requestedFactorDataModel,
        riskCommandsRef,
        selectedFactorModel,
        suspendCoreLoad,
        userDataReady,
        userUid,
        watchlistActualSyncVersion,
        bootstrapSelectionsReady,
    ]);
}
